/**
 * Map Vote Node - Player View
 *
 * Tactical briefing-style map selection. Dark, high-contrast map cards
 * with satellite-imagery feel, animated vote bars, and brand-500 teal
 * glow on selection. Matches the ReadyUp quality bar.
 */

import { AnimatePresence, motion } from "framer-motion";
import { Check, ImageOff, Users } from "lucide-react";
import { useState } from "react";
import { trpc } from "../../../lib/trpc";
import { cn } from "../../../lib/utils";
import { ErrorBanner } from "../../components/ErrorBanner";
import { NodeCard } from "../../components/NodeCard";
import { NodeHeader } from "../../components/NodeHeader";
import { useCurrentPlayerId } from "../../hooks/useCurrentPlayerId";
import type { NodePlayerViewProps } from "../types";

type MapVoteState = {
  votes: Record<string, string>;
  voteCounts: Record<string, number>;
  mapPool: string[];
  mapDetails?: Record<string, { name: string; imageUrl: string | null }>;
};

export function MapVoteView({
  nodeState,
  players,
  context,
  onAction,
}: NodePlayerViewProps<MapVoteState>) {
  const userId = useCurrentPlayerId();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const state = nodeState.state as MapVoteState;
  const config = nodeState.config as {
    showVotes?: "hidden" | "count_only" | "full";
    labels?: { title?: string; subtitle?: string };
  };
  const title = config.labels?.title ?? "Map Selection";
  const subtitle = config.labels?.subtitle ?? "Vote for the battlefield";
  const showVotes = config.showVotes ?? "count_only";
  const myVote = state.votes[userId || ""];

  // Fetch map details from tRPC, fall back to state-provided mapDetails (dev preview)
  const { data: mapsFromApi } = trpc.game.getMaps.useQuery(
    { gameId: (context.gameId as string) || "" },
    { enabled: !!(context.gameId as string) },
  );

  const getMapInfo = (mapId: string) => {
    const fromApi = mapsFromApi?.find((m) => m.id === mapId);
    if (fromApi) return { name: fromApi.name, imageUrl: fromApi.imageUrl ?? null };
    const fromState = state.mapDetails?.[mapId];
    if (fromState) return fromState;
    return { name: mapId, imageUrl: null };
  };

  const handleVote = async (mapId: string) => {
    setIsSubmitting(true);
    setError(null);

    const result = await onAction("vote", { mapId, userId });

    if (!result.success && result.error) {
      setError(result.error);
    }

    setIsSubmitting(false);
  };

  const voteCount = Object.keys(state.votes).length;
  const totalPlayers = players.length;
  const totalVotesCast = Object.values(state.voteCounts).reduce((s, n) => s + n, 0);

  return (
    <NodeCard>
      <NodeHeader title={title} subtitle={subtitle} />

      {/* Vote count pill */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.15 }}
        className="text-center mb-6"
      >
        <div className="inline-flex items-center gap-3 px-4 py-2 rounded-lg bg-surface-800/80 border border-surface-700/50">
          <span className="font-display text-2xl font-bold text-text-primary tabular-nums">
            {voteCount}
          </span>
          <span className="text-surface-500 text-sm">/</span>
          <span className="font-display text-2xl font-bold text-text-primary tabular-nums">
            {totalPlayers}
          </span>
          <span className="text-xs uppercase tracking-wider text-text-secondary font-medium">
            voted
          </span>
        </div>
      </motion.div>

      <ErrorBanner error={error} />

      {/* Map cards grid */}
      <div className="grid grid-cols-1 @md:grid-cols-2 @3xl:grid-cols-3 gap-4 @md:gap-5">
        {state.mapPool.map((mapId, index) => {
          const info = getMapInfo(mapId);
          const votes = state.voteCounts[mapId] ?? 0;
          const isSelected = myVote === mapId;
          const votePercent = totalVotesCast > 0 ? (votes / totalVotesCast) * 100 : 0;

          return (
            <MapCard
              key={mapId}
              mapId={mapId}
              name={info.name}
              imageUrl={info.imageUrl}
              votes={votes}
              votePercent={votePercent}
              isSelected={isSelected}
              showVotes={showVotes}
              disabled={isSubmitting}
              index={index}
              onVote={handleVote}
            />
          );
        })}
      </div>
    </NodeCard>
  );
}

// ---------------------------------------------------------------------------
// Map card component
// ---------------------------------------------------------------------------

type MapCardProps = {
  mapId: string;
  name: string;
  imageUrl: string | null;
  votes: number;
  votePercent: number;
  isSelected: boolean;
  showVotes: "hidden" | "count_only" | "full";
  disabled: boolean;
  index: number;
  onVote: (mapId: string) => void;
};

function MapCard({
  mapId,
  name,
  imageUrl,
  votes,
  votePercent,
  isSelected,
  showVotes,
  disabled,
  index,
  onVote,
}: MapCardProps) {
  const delay = Math.min(0.1 + index * 0.06, 0.5);

  return (
    <motion.button
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay }}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => onVote(mapId)}
      disabled={disabled}
      className={cn(
        "group relative rounded-lg overflow-hidden text-left",
        "transition-all duration-200 cursor-pointer",
        "focus:outline-none focus:ring-2 focus:ring-brand-400 focus:ring-offset-2 focus:ring-offset-surface-900",
        "disabled:cursor-not-allowed disabled:opacity-60",
        isSelected
          ? "ring-2 ring-brand-500/70 shadow-[0_0_20px_rgba(38,217,181,0.15),0_0_40px_rgba(38,217,181,0.05)]"
          : "ring-1 ring-surface-700/50 hover:ring-surface-500/60",
      )}
    >
      {/* Map image */}
      <div className="relative aspect-video bg-surface-800 overflow-hidden">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={name}
            className={cn(
              "w-full h-full object-cover transition-all duration-300",
              isSelected
                ? "brightness-110 saturate-110"
                : "brightness-75 saturate-90 group-hover:brightness-90 group-hover:saturate-100",
            )}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-surface-800">
            <ImageOff size={32} className="text-surface-600" aria-hidden="true" />
          </div>
        )}

        {/* Bottom gradient for text readability */}
        <div className="absolute inset-x-0 bottom-0 h-3/4 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />

        {/* Dark overlay when not selected */}
        {!isSelected && (
          <div className="absolute inset-0 bg-surface-950/30 group-hover:bg-surface-950/10 transition-colors duration-200" />
        )}

        {/* Map name — over image bottom */}
        <div className="absolute inset-x-0 bottom-0 p-3 @md:p-4">
          <p
            className="font-display font-bold text-white text-lg @sm:text-xl leading-tight"
            style={{ textShadow: "0 2px 6px rgba(0,0,0,0.7)" }}
          >
            {name}
          </p>
        </div>

        {/* Vote count badge */}
        {showVotes !== "hidden" && (
          <div className="absolute top-3 right-3">
            <motion.div
              key={votes}
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              className={cn(
                "flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm font-bold tabular-nums",
                "backdrop-blur-md shadow-lg",
                isSelected
                  ? "bg-black/60 text-brand-300 border border-brand-500/40"
                  : "bg-black/60 text-white border border-white/10",
              )}
            >
              <Users
                className={cn("size-3.5", isSelected ? "text-brand-300" : "text-surface-300")}
              />
              {votes}
            </motion.div>
          </div>
        )}

        {/* Selected checkmark */}
        <AnimatePresence>
          {isSelected && (
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0 }}
              transition={{ type: "spring", stiffness: 400, damping: 20 }}
              className="absolute top-3 left-3 w-8 h-8 rounded-full bg-brand-500 flex items-center justify-center shadow-[0_0_16px_rgba(38,217,181,0.6)]"
            >
              <Check size={12} strokeWidth={3} className="text-surface-900" aria-hidden="true" />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Selected glow border at top */}
        {isSelected && (
          <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-brand-400/60 to-transparent" />
        )}
      </div>

      {/* Vote bar at card bottom */}
      {showVotes === "full" && (
        <div className="h-1.5 bg-surface-800 relative overflow-hidden">
          <motion.div
            className="absolute inset-y-0 left-0 bg-gradient-to-r from-brand-500 to-brand-400"
            initial={{ width: 0 }}
            animate={{ width: `${votePercent}%` }}
            transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
          />
        </div>
      )}
    </motion.button>
  );
}
